import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { parseEBUScoreCSV } from '../utils/ebuParser';
import { parsePBN } from '../utils/pbnParser';
import './AdminUpload.css';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'bridge2026';

function AdminUpload() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [pbnFile, setPbnFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  function handleLogin(e) {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setMessage('');
    } else {
      setMessage('Incorrect password');
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    
    if (!csvFile) {
      setMessage('Please select a CSV file');
      return;
    }

    setUploading(true);
    setMessage('Processing files...');

    try {
      // Read CSV file
      const csvText = await csvFile.text();
      const parsedData = parseEBUScoreCSV(csvText);

      // Read PBN file if provided
      let handRecords = null;
      if (pbnFile) {
        const pbnText = await pbnFile.text();
        handRecords = parsePBN(pbnText);
      }

      // Prepare game data
      const gameData = {
        title: parsedData.metadata.Title || 'Untitled Game',
        game_date: parseDate(parsedData.metadata.Date),
        game_type: parsedData.gameType,
        num_boards: parseInt(parsedData.metadata.Boards) || 0,
        director: parsedData.metadata.Director?.trim() || null,
        scores: parsedData.scores,
        travellers: parsedData.travellers,
        hand_records: handRecords,
        metadata: parsedData.metadata,
      };

      // Insert into Supabase
      const { error } = await supabase
        .from('games')
        .insert([gameData])
        .select();

      if (error) throw error;

      setMessage(`Successfully uploaded: ${gameData.title}`);
      setCsvFile(null);
      setPbnFile(null);
      
      // Reset file inputs
      document.getElementById('csv-input').value = '';
      if (document.getElementById('pbn-input')) {
        document.getElementById('pbn-input').value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  function parseDate(dateStr) {
    // EBUScore format: DD/MM/YYYY
    if (!dateStr) return new Date().toISOString().split('T')[0];
    
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return new Date().toISOString().split('T')[0];
  }

  async function handleDeleteAll() {
    const confirmed = window.confirm(
      'WARNING: This will permanently delete ALL games from the database. This action cannot be undone. Are you absolutely sure?'
    );
    
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      'This is your final warning. Click OK to delete all games permanently.'
    );
    
    if (!doubleConfirm) return;
    
    setUploading(true);
    setMessage('Deleting all games...');
    
    try {
      // Delete all games
      const { error } = await supabase
        .from('games')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all rows
      
      if (error) throw error;
      
      setMessage('All games have been deleted successfully.');
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(`Error deleting games: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  if (!authenticated) {
    return (
      <div className="container admin-login">
        <h2>Admin Login</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Password:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit">Login</button>
          {message && <p className="error-message">{message}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="container admin-upload">
      <h2>Upload Game Results</h2>
      
      <form onSubmit={handleUpload}>
        <div className="form-group">
          <label>EBUScore CSV File (required):</label>
          <input
            id="csv-input"
            type="file"
            accept=".csv"
            onChange={(e) => {
              setCsvFile(e.target.files[0]);
            }}
            required
          />
          <small>Export from EBUScore as CSV</small>
        </div>

        <div className="form-group">
          <label>PBN Hand Records (optional):</label>
          <input
            id="pbn-input"
            type="file"
            accept=".pbn"
            onChange={(e) => {
              setPbnFile(e.target.files[0]);
            }}
          />
          <small>Optional: Include hand diagrams</small>
        </div>

        <button type="submit" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Game'}
        </button>

        {message && (
          <p className={message.startsWith('Error') ? 'error-message' : 'success-message'}>
            {message}
          </p>
        )}
      </form>

      <div className="danger-zone">
        <h3>Danger Zone</h3>
        <button 
          type="button" 
          onClick={handleDeleteAll} 
          disabled={uploading}
          className="delete-all-btn"
        >
          Delete All Games
        </button>
        <p className="warning-text">⚠️ This will permanently delete all games from the database</p>
      </div>

      <div className="upload-info">
        <h3>Instructions:</h3>
        <ol>
          <li>Export your game results from EBUScore as a CSV file</li>
          <li>Optionally, export hand records as a PBN file</li>
          <li>Upload both files here</li>
          <li>Results will be immediately available to players</li>
        </ol>
      </div>
    </div>
  );
}

export default AdminUpload;
